import { useCallback, useState } from 'react';
import { message } from 'antd';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { useBatchTransactionStore } from '../stores/batch-transaction-store';
import type { BatchConfigDTO, BatchConfigImportRequest, BatchConfigImportResult } from '../types';

// ── API ───────────────────────────────────────────────────────────────────

const apiListConfigs = (): Promise<BatchConfigDTO[]> =>
  customInstance({ url: '/api/batch-transactions/configs', method: 'GET' });

const apiGetConfig = (id: number): Promise<BatchConfigDTO> =>
  customInstance({ url: `/api/batch-transactions/configs/${id}`, method: 'GET' });

const apiCreateConfig = (dto: BatchConfigDTO): Promise<BatchConfigDTO> =>
  customInstance({ url: '/api/batch-transactions/configs', method: 'POST', data: dto });

const apiUpdateConfig = (id: number, dto: BatchConfigDTO): Promise<BatchConfigDTO> =>
  customInstance({ url: `/api/batch-transactions/configs/${id}`, method: 'PUT', data: dto });

const apiDeleteConfig = (id: number): Promise<void> =>
  customInstance({ url: `/api/batch-transactions/configs/${id}`, method: 'DELETE' });

const apiImportConfig = (req: BatchConfigImportRequest): Promise<BatchConfigImportResult> =>
  customInstance({ url: '/api/batch-transactions/configs/import', method: 'POST', data: req });

const apiExportConfig = (id: number): Promise<Blob> =>
  customInstance({
    url: `/api/batch-transactions/configs/${id}/export`,
    method: 'GET',
    responseType: 'blob',
  });

// ── Hook ──────────────────────────────────────────────────────────────────

export const useBatchConfig = () => {
  const { configs, configsLoading, setConfigs, setConfigsLoading } = useBatchTransactionStore();
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadConfigs = useCallback(async () => {
    setConfigsLoading(true);
    try {
      const data = await apiListConfigs();
      setConfigs(data);
    } catch {
      message.error('Failed to load batch configurations');
    } finally {
      setConfigsLoading(false);
    }
  }, [setConfigs, setConfigsLoading]);

  const createConfig = useCallback(async (dto: BatchConfigDTO): Promise<BatchConfigDTO | null> => {
    setSaving(true);
    try {
      const saved = await apiCreateConfig(dto);
      message.success('Configuration created successfully');
      await loadConfigs();
      return saved;
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to create configuration');
      return null;
    } finally {
      setSaving(false);
    }
  }, [loadConfigs]);

  const updateConfig = useCallback(
    async (id: number, dto: BatchConfigDTO): Promise<BatchConfigDTO | null> => {
      setSaving(true);
      try {
        const updated = await apiUpdateConfig(id, dto);
        message.success('Configuration updated successfully');
        await loadConfigs();
        return updated;
      } catch (err: any) {
        message.error(err?.response?.data?.message || 'Failed to update configuration');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [loadConfigs]
  );

  const deleteConfig = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        await apiDeleteConfig(id);
        message.success('Configuration deleted');
        await loadConfigs();
        return true;
      } catch {
        message.error('Failed to delete configuration');
        return false;
      }
    },
    [loadConfigs]
  );

  const exportConfig = useCallback(async (id: number, code: string) => {
    try {
      const blob = await apiExportConfig(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch-config-${code}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to export configuration');
    }
  }, []);

  const importConfig = useCallback(
    async (req: BatchConfigImportRequest): Promise<BatchConfigImportResult | null> => {
      setImporting(true);
      try {
        const result = await apiImportConfig(req);
        if (result.success) {
          message.success(result.message);
          await loadConfigs();
        } else {
          message.warning(result.message);
        }
        return result;
      } catch (err: any) {
        message.error(err?.response?.data?.message || 'Import failed');
        return null;
      } finally {
        setImporting(false);
      }
    },
    [loadConfigs]
  );

  return {
    configs,
    configsLoading,
    saving,
    importing,
    loadConfigs,
    createConfig,
    updateConfig,
    deleteConfig,
    exportConfig,
    importConfig,
  };
};
